"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, CircleCheck as CheckCircle2, Droplets, Flame, PhoneCall, Pipette, ShieldAlert, Wrench, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface ProblemCategory {
  id: string;
  title: string;
  badge: string;
  badgeVariant: "destructive" | "secondary" | "default" | "outline";
  urgency: string;
  description: string;
  commonSigns: string;
  iconName: "droplets" | "pipette" | "flame" | "shield" | "alert" | "wrench";
  linkPath: string;
}
const problemCategories: ProblemCategory[] = [{
  id: "burst-pipes",
  title: "Burst & Leaking Pipes",
  badge: "Immediate Threat",
  badgeVariant: "destructive",
  urgency: "Dispatch within 45 mins",
  description: "Active leaks behind walls, ceiling drips, or burst main supply lines that can cause severe structural flood damage.",
  commonSigns: "Puddles near baseboards, spinning water meter, hissing sounds in walls",
  iconName: "droplets",
  linkPath: "/problems/"
}, {
  id: "sewer-drain-backup",
  title: "Clogged Drains & Backups",
  badge: "Biohazard Risk",
  badgeVariant: "destructive",
  urgency: "Same-Day Priority",
  description: "Main sewer backups or persistent multi-fixture blockages causing contaminated wastewater to rise into your home.",
  commonSigns: "Gurgling toilets, foul sewer odor, sewage rising in showers or tubs",
  iconName: "pipette",
  linkPath: "/problems/"
}, {
  id: "water-heater-failure",
  title: "Water Heater Emergencies",
  badge: "High Urgency",
  badgeVariant: "default",
  urgency: "24/7 Rapid Repair",
  description: "Total loss of hot water, ruptured expansion tanks, tank base leaks, or strange popping and banging noises.",
  commonSigns: "Rusty tap water, standing water under tank, pilot light won't stay lit",
  iconName: "flame",
  linkPath: "/water-heater/"
}, {
  id: "toilet-overflows",
  title: "Severe Toilet Overflows",
  badge: "Urgent",
  badgeVariant: "secondary",
  urgency: "Immediate Dispatch",
  description: "Toilets that refuse to drain, continuously overflow, or have broken shutoff valves during a backup cycle.",
  commonSigns: "Water rising past rim, stubborn obstructions, tank refilling non-stop",
  iconName: "shield",
  linkPath: "/problems/"
}, {
  id: "low-pressure",
  title: "Sudden Loss of Pressure",
  badge: "Diagnostic Need",
  badgeVariant: "outline",
  urgency: "Fast Response",
  description: "Abrupt drops in household water pressure signaling hidden slab leaks, corroded piping, or municipal line faults.",
  commonSigns: "Weak shower spray, trickling faucets, whistling supply pipes",
  iconName: "alert",
  linkPath: "/problems/"
}, {
  id: "fixture-failure",
  title: "Broken Valves & Fixtures",
  badge: "Repair & Replacement",
  badgeVariant: "secondary",
  urgency: "Same-Day Service",
  description: "Snapped emergency shutoff valves, cracked hose bibbs, or leaking garbage disposals flooding cabinetry.",
  commonSigns: "Seized shutoff handles, constant under-sink moisture, dripping hose bibbs",
  iconName: "wrench",
  linkPath: "/services/"
}];
export default function RelatedProblems() {
  const shouldReduceMotion = useReducedMotion();
  const getCategoryIcon = (name: ProblemCategory["iconName"]) => {
    switch (name) {
      case "droplets":
        return <Droplets className="size-6 text-primary" />;
      case "pipette":
        return <Pipette className="size-6 text-primary" />;
      case "flame":
        return <Flame className="size-6 text-primary" />;
      case "shield":
        return <ShieldAlert className="size-6 text-primary" />;
      case "alert":
        return <AlertTriangle className="size-6 text-primary" />;
      case "wrench":
        return <Wrench className="size-6 text-primary" />;
      default:
        return <Zap className="size-6 text-primary" />;
    }
  };
  return <section id="relatedproblemcategories" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 text-xs uppercase tracking-wider font-semibold">
            Urgent Troubleshooting
          </Badge>
          <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Related Plumbing Problems
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Plumbing disasters rarely wait for business hours. Browse specific failure categories below to diagnose your issue or get immediate assistance from our Jonesboro dispatch team.
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {problemCategories.map((category, index) => <motion.div key={category.id} data-index={index} initial={shouldReduceMotion ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.4,
          delay: index * 0.08
        }} className="flex">
              <Card className="flex flex-col justify-between w-full border border-border/80 bg-card hover:border-primary/40 transition-colors shadow-xs hover:shadow-md">
                <CardHeader className="space-y-3 pb-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="p-3 rounded-lg bg-secondary text-secondary-foreground">
                      {getCategoryIcon(category.iconName)}
                    </div>
                    <Badge variant={category.badgeVariant} className="text-xs">
                      {category.badge}
                    </Badge>
                  </div>
                  <div>
                    <CardTitle className="font-default text-xl font-bold mt-2">
                      {category.title}
                    </CardTitle>
                    <CardDescription className="text-xs font-semibold text-accent-foreground mt-1 flex items-center gap-1">
                      <Zap className="size-3.5 text-accent shrink-0" />
                      {category.urgency}
                    </CardDescription>
                  </div>
                </CardHeader>

                <CardContent className="space-y-4 text-sm flex-1">
                  <p className="text-muted-foreground leading-relaxed">
                    {category.description}
                  </p>

                  <div className="pt-2 border-t border-border/60">
                    <span className="text-xs font-semibold uppercase tracking-wide text-foreground/80 block mb-1.5">
                      Common Warning Signs:
                    </span>
                    <p className="text-xs text-muted-foreground bg-muted/60 p-2.5 rounded-md leading-normal flex items-start gap-1.5">
                      <CheckCircle2 className="size-3.5 text-primary shrink-0 mt-0.5" />
                      <span>{category.commonSigns}</span>
                    </p>
                  </div>
                </CardContent>

                <CardFooter className="pt-2 border-t border-border/60 flex items-center justify-between">
                  <Link to={category.linkPath} className="text-xs font-semibold text-primary hover:underline inline-flex items-center gap-1 group">
                    View problem guide
                    <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
                  </Link>

                  <Button asChild size="sm" variant="secondary" className="text-xs h-8">
                    <Link to="/contact/">
                      Get Help
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Emergency Assistance Banner */}
        <div className="mt-12 md:mt-16 bg-primary text-primary-foreground rounded-2xl p-6 sm:p-8 lg:p-10 shadow-lg border border-primary/20">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="space-y-2 text-center lg:text-left max-w-2xl">
              <h3 className="font-default text-2xl sm:text-3xl font-bold tracking-tight">
                Not sure what kind of plumbing issue you have?
              </h3>
              <p className="text-primary-foreground/80 text-sm sm:text-base">
                Call our Jonesboro emergency operators right now. We can guide you to turn off your main water valve and dispatch a licensed technician immediately.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3.5 w-full lg:w-auto justify-center">
              <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto font-semibold">
                <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>Request Emergency Plumber</span>
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="w-full sm:w-auto bg-transparent text-primary-foreground border-primary-foreground/30 hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <Link to="/services/">
                  All Services
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}